import React, { useEffect, useRef } from 'react';
import { isWeb } from '~global-screens';
import PropTypes from 'prop-types';
import { useAdSenseFallback } from './useAdSenseFallback';
import AdsPlaceholderImage from './AdsPlaceholderImage';
import { Text } from 'react-native';

const Config = require('../../../../config/default.env').default;

export default function AdsenseParallax({
  adSlot,
  style = {},
  adFormat = 'fluid',
  adLayout = 'in-article',
}) {
  const adContainerRef = useRef(null);
  const adId = useRef(
    `adsense-parallax-${adSlot}-${Math.random().toString(36).substring(2, 9)}`,
  ).current;

  const shouldShowFallback = useAdSenseFallback(adId);

  // When the ad is filled, allow the container to resize to the ad's natural height.
  useEffect(() => {
    if (adContainerRef.current && !shouldShowFallback) {
      adContainerRef.current.style.height = 'auto';
    }
  }, [shouldShowFallback]);

  if (!Config.GOOGLE_ADSENSE_ENABLED) {
    return null;
  }

  const adProps = {
    key: adId,
    id: adId,
    className: 'adsbygoogle',
    style: {
      display: 'block',
      textAlign: 'center',
    },
    'data-ad-client': Config.GOOGLE_ADSENSE_PUBLISHER_ID,
    'data-ad-slot': adSlot,
    'data-ad-format': adFormat,
    'data-ad-layout': adLayout,
    'data-full-width-responsive': 'true',
  };

  const webStyles = {
    container: {
      ...style,
      width: '100%',
      // Set an initial height to prevent layout shift while the ad loads.
      // This will be set to 'auto' by the useEffect once the ad is filled.
      height: isWeb ? '300px' : '250px',
      position: 'relative',
      overflow: 'hidden',
    },
    placeholder: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 10,
      display: 'flex',
      padding: isWeb ? 20 : 10,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f9f9f9',
    },
    placeholderText: {
      color: '#666',
      fontSize: '14px',
      fontFamily: "'Work Sans', sans-serif",
    },
  };

  return (
    <div id="adsense-parallax" ref={adContainerRef} style={webStyles.container}>
      <ins {...adProps} />
      {shouldShowFallback && (
        <div style={webStyles.placeholder}>
          <AdsPlaceholderImage />
          <Text style={webStyles.placeholderText}>
            Keep scrolling to continue the story
          </Text>
        </div>
      )}
    </div>
  );
}

AdsenseParallax.propTypes = {
  adSlot: PropTypes.string.isRequired,
  style: PropTypes.object,
  adFormat: PropTypes.string,
  adLayout: PropTypes.string,
};
